<script>
import appConfig from '@src/app.config'
import 'bootstrap'
import NavBar from '@components/nav-bar'
import Utils from '@lib/util'
import { mapState } from 'vuex'

import {
  SidebarToggler,
  Sidebar,
  SidebarFooter,
  SidebarForm,
  SidebarHeader,
  SidebarMinimizer,
  SidebarNav,
} from '@components/layout/SideBar'

export default {
  components: {
    NavBar,
    Sidebar,
    SidebarForm,
    SidebarFooter,
    SidebarToggler,
    SidebarHeader,
    SidebarNav,
    SidebarMinimizer,
  },
  page: {
    // All subcomponent titles will be injected into this template.
    titleTemplate(title) {
      title = typeof title === 'function' ? title(this.$store) : title
      return title ? `${title} | ${appConfig.title}` : appConfig.title
    },
  },
  data() {
    return {
      items: [
        {
          name: 'Dashboard',
          url: '/dashboard',
          icon: 'home',
        },

        // === CMS ===

        {
          name: 'Obsah',
          url: '/content',
          icon: 'list',
          feature: 'Intranet',
          permissionsNames: [
            'Pages_Administration', 
            'Posts_Administration',
            'Gallery_Administration'
            ],
          children: [
            {
              name: 'Stránky',
              url: '/content/pages',
              icon: 'columns',
              feature: 'Intranet',
              permissionsNames: ['Pages_Administration'],
            },
            {
              name: 'Aktuality a články',
              url: '/content/articles',
              icon: 'newspaper',
              feature: 'Intranet',
              permissionsNames: ['Posts_Administration']
            },
            {
              name: 'Galerie',
              url: '/content/gallery',
              icon: 'images',
              feature: 'Intranet',
              permissionsNames: ['Gallery_Administration']
            },
          ],
        },

        // === SETTINGS ===

        {
          name: 'Nastavení',
          url: '/setting',
          icon: 'cogs',
          feature: 'Intranet',
          permissionsNames: [
            'Classes_Administration',
            'Users_Administration',
            'Roles_Administration',
            'Navigation_Administration_Editing',
            'Navigation_Administration'
          ],
          children: [
            {
              name: 'Třídy',
              url: '/setting/classes',
              icon: 'chalkboard-teacher',
              feature: 'Intranet',
              permissionsNames: ['Classes_Administration']
            },
            {
              name: 'Správa uživatelů',
              url: '/setting/users',
              icon: 'users',
              feature: 'Intranet',
              permissionsNames: ['Users_Administration']
            },
            {
              name: 'Správa rolí',
              url: '/setting/rolesAndPermissions',
              icon: 'user-shield',
              feature: 'Intranet',
              permissionsNames: ['Roles_Administration']
            },
            {
              name: 'Správa navigace',
              url: '/setting/navigation',
              icon: 'list-alt',
              feature: 'Intranet',
              permissionsNames: [
                'Navigation_Administration_Editing',
                'Navigation_Administration']
            },
          ],
        },

        // === SYSTEM ===

        {
          name: 'Systém',
          url: '/system',
          icon: 'tools',
          feature: 'Intranet',
          permissionsNames: [
            'System_Administration'
          ],
          children: [
            {
              name: 'Naplánované úlohy',
              url: '/system/jobs',
              icon: 'clock',
              feature: 'Intranet',
              permissionsNames: ['System_Administration']
            },
            {
              name: 'Logování',
              url: '/system/logs',
              icon: 'file-archive',
              feature: 'Intranet',
              permissionsNames: ['System_Administration']
            },
            {
              name: 'Nastavení',
              url: '/system/settings',
              icon: 'sliders-h',
              feature: 'Intranet',
              permissionsNames: ['System_Administration']
            },
            {
              name: 'Systémové informace',
              url: '/system/systemInfo',
              icon: 'sliders-h',
              feature: 'Intranet',
              permissionsNames: ['System_Administration']
            },
          ],
        },
      ],
    }
  },
  computed: {
    ...mapState('auth', {
      loggedUser: (state) => state.currentUser.user,
    })
  },
  methods:{
    logOut() {
      this.$store.dispatch("auth/logOut", this);
      Utils.abp.auth.clearToken();
      this.$router.push({name: 'login' });
    },
  }
}
</script>

<template>
  <div id="app" class="app">
    <header v-if="$route.name != 'login'" class="app-header navbar">
      <b-Link class="navbar-brand" to="/">
        <div class="navbar-brand-full"><img class="nav-logo" src="./assets/images/UPlogo.png" /></div>
        <!-- <div class="navbar-brand-minimized">UEPP</div> -->
      </b-Link>
      <sidebar-toggler display="md" class="d-lg-none" mobile />
      <b-navbar-nav class="ml-auto">
        <b-nav-item class="d-md-down-none">
          <i class="fa fa-user"></i>
          {{ loggedUser.fullName }} <small>({{ loggedUser.userName }})</small>
        </b-nav-item>
        <b-nav-item class="d-md-down-none" @click="logOut" title="Odhlásit">
          <i class="fa fa-lock"></i>
        </b-nav-item>
        <!-- <DefaultHeaderDropdownAccnt/> -->
      </b-navbar-nav>
    </header>

    <div class="app-body">
      <Sidebar fixed v-if="$route.name != 'login'">
        <SidebarHeader/>
        <SidebarForm/>
        <SidebarNav :nav-items="items"></SidebarNav>
        <SidebarFooter/>
        <SidebarMinimizer/>
      </Sidebar>
      <div class="main">
        <b-container fluid class="padding-none">
          <!--
          Even when routes use the same component, treat them
          as distinct and create the component again.
          -->
          <RouterView :key="$route.fullPath"/>
        </b-container>
      </div>
    </div>
  </div>
</template>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
// Allow element/type selectors, because this is global CSS.
// stylelint-disable selector-max-type, selector-class-pattern
@import '~@fortawesome/fontawesome-free/css/all.min.css';
// @import '~@fortawesome/fontawesome-free/scss/fontawesome.scss';
// @import '~@fortawesome/fontawesome-free/scss/solid.scss';
// @import '~@fortawesome/fontawesome-free/scss/brands.scss';

@import '~bootstrap/scss/bootstrap';
@import '~bootstrap-vue/dist/bootstrap-vue.css';
@import './assets/coreui/coreui-standalone.min.css';
// Normalize default styles across browsers,
// https://necolas.github.io/normalize.css/
@import '~normalize.css/normalize.css';
// Style loading bar between pages.
// https://github.com/rstacruz/nprogress
@import '~nprogress/nprogress.css';
@import '~vue-multiselect/dist/vue-multiselect.min.css';

// Design variables and utilities from src/design.
@import '@design';

*,
*::before,
*::after {
  box-sizing: border-box;
}

body {
  background: $color-body-bg;
}
#app {
  @extend %typography-small;
}

// ===
// Base element styles
// ===

// a,
// a:visited {
//   color: $color-link-text;
// }

h1 {
  @extend %typography-xxlarge;
}

h2 {
  @extend %typography-xlarge;
}

h3 {
  @extend %typography-large;
}

h4 {
  @extend %typography-medium;
}

h5,
h6 {
  @extend %typography-small;
}

// ===
// Vendor
// ===

#nprogress .bar {
  background: $color-link-text;
}
</style>
